/**
 * routes/LoginScreen.jsx -- Port de la seccion #screen-login de
 * app/frontend/index.html + la logica de wireLoginScreen()/
 * wirePasswordToggle()/showLoginError() en src/js/auth.js.
 *
 * Diferencia real con el original: ahi el HTML del login vive siempre en
 * el DOM (oculto con la clase `.hidden` hasta que bootstrap() decide
 * mostrarlo). Aqui React simplemente no MONTA este componente hasta que
 * <App> ya sabe que no hay sesion -- mismo resultado (cero parpadeo),
 * mecanismo mas simple (no hay que acordarse de ocultar/mostrar nada).
 */
import { useEffect, useRef, useState } from 'react';
import { useMagneticScope } from '../hooks/useMagneticScope.js';
import { api } from '../lib/api.js';
import { setSession, consumeSessionExpiredNotice } from '../lib/sessionStore.js';
import { applyTheme, storeLoginTheme } from '../theme/theme.js';

export default function LoginScreen({ institutionalBrandLabel }) {
  const scope = useRef(null);
  useMagneticScope(scope);
  const [usuario, setUsuario] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [noticeShown, setNoticeShown] = useState('');

  useEffect(() => {
    // Mismo aviso que showExpiredNoticeIfAny() en auth.js, leido una sola
    // vez al montar (equivalente a "cuando se decide mostrar login").
    if (consumeSessionExpiredNotice()) {
      setNoticeShown('Tu sesión venció por inactividad. Ingresa nuevamente.');
    }
  }, []);

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    const trimmedUser = usuario.trim();
    if (!trimmedUser || !password) {
      setError('Captura tu usuario y contraseña.');
      return;
    }
    setBusy(true);
    try {
      const result = await api.login(trimmedUser, password, 'INTERNO');
      if (!result.ok) {
        setError(result.error || 'No se pudo iniciar sesión.');
        return;
      }
      setSession(result);
      applyTheme(result.temaPreferido);
      storeLoginTheme(result.temaLogin);
      // No hace falta navegar: <App> ve el cambio de sesion (useSession)
      // y renderiza el shell solo -- mismo espiritu que startApp() en el
      // original, pero sin tener que llamarlo explicitamente.
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="auth-layout magnetic-scope" ref={scope}>
      <div className="auth-hero">
        <div className="auth-hero-content">
          <span className="auth-hero-badge">Operaciones Last Mile 360</span>
          <h2 className="auth-hero-title">Centraliza tu operación con Last Mile en un solo lugar</h2>
          <p className="auth-hero-text">
            Un solo sistema para coordinar pedidos, incidencias y catálogos entre tiendas y equipos Last Mile.
          </p>
          <ul className="auth-hero-points">
            <li><span className="material-symbols-rounded" aria-hidden="true">local_shipping</span>Registro y seguimiento de pedidos</li>
            <li><span className="material-symbols-rounded" aria-hidden="true">report</span>Reportes de incidencias</li>
            <li><span className="material-symbols-rounded" aria-hidden="true">category</span>Catálogos Last Mile</li>
          </ul>
        </div>
      </div>

      <div className="auth-panel">
        <main id="main-content" tabIndex={-1} className="auth-card">
          <div className="auth-brand">
            <span className="auth-brand-mark" aria-hidden="true"><span className="material-symbols-rounded">local_shipping</span></span>
            <span>
              <span className="auth-brand-name">Operaciones Last Mile 360</span><br />
              <span className="auth-brand-sub">{institutionalBrandLabel}</span>
            </span>
          </div>

          <h1 className="auth-title">Iniciar sesión</h1>
          <p className="auth-subtitle">Ingresa con tu usuario y contraseña.</p>

          {(error || noticeShown) && (
            <p className="auth-alert" role="alert">
              <span className="material-symbols-rounded" aria-hidden="true">error</span>
              <span>{error || noticeShown}</span>
            </p>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="auth-field">
              <label className="form-label" htmlFor="login-usuario">Usuario</label>
              <div className="auth-input-wrap">
                <span className="material-symbols-rounded auth-input-icon" aria-hidden="true">person</span>
                <input
                  id="login-usuario" name="usuario" className="form-input auth-input"
                  autoComplete="username" autoCapitalize="none" spellCheck="false" required
                  value={usuario} onChange={(e) => setUsuario(e.target.value.replace(/\s/g, ''))}
                />
              </div>
            </div>

            <div className="auth-field">
              <label className="form-label" htmlFor="login-password">Contraseña</label>
              <div className="auth-input-wrap">
                <span className="material-symbols-rounded auth-input-icon" aria-hidden="true">lock</span>
                <input
                  id="login-password" name="password" type={showPassword ? 'text' : 'password'}
                  className="form-input auth-input auth-input-pass" minLength={8} maxLength={12}
                  autoComplete="current-password" required
                  value={password} onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button" className="auth-toggle" aria-pressed={showPassword}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  <span className="material-symbols-rounded" aria-hidden="true">{showPassword ? 'visibility_off' : 'visibility'}</span>
                </button>
              </div>
            </div>

            <button className="btn btn-primary auth-submit" type="submit" disabled={busy}>
              {busy && <span className="spinner" aria-hidden="true"></span>}
              Iniciar sesión
            </button>
          </form>

          <p className="auth-help">
            <span className="material-symbols-rounded auth-help-icon" aria-hidden="true">support_agent</span>
            <span>Si tienes dudas sobre cómo ingresar al sistema, contacta al Subgerente Last Mile.</span>
          </p>

          <p className="auth-footer">Walmart de México y Centroamérica &middot; Uso interno</p>
        </main>
      </div>
    </section>
  );
}
